import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useOrg } from '../org/context';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { Spinner } from '../components/Spinner';
import { toDisplayError } from '../api/errorMessages';
import styles from './RequireOrg.module.css';

/**
 * Guards org-scoped screens. Distinguishes four states so an errored load is
 * never mistaken for "no orgs":
 *  - idle/loading -> a status region + spinner
 *  - error        -> an accessible, retryable error (NOT a create-org redirect)
 *  - ready + empty -> redirect to /getting-started (org creation is its step 1)
 *  - ready + orgs  -> render children
 */
export function RequireOrg({ children }: { children: ReactNode }) {
  const { status, orgs, error, refresh } = useOrg();

  if (status === 'error') {
    const display = toDisplayError(error);
    return (
      <AppShell>
        <Card>
          <h1 className={styles.heading}>We couldn’t load your organizations</h1>
          <div className={styles.summary}>
            <InlineAlert severity="error" {...(display.requestId ? { requestId: display.requestId } : {})}>
              {display.message}
            </InlineAlert>
          </div>
          <Button variant="secondary" onClick={() => void refresh().catch(() => {})}>
            Try again
          </Button>
        </Card>
      </AppShell>
    );
  }

  if (status === 'ready' && orgs.length === 0) {
    return <Navigate to="/getting-started" replace />;
  }

  if (status !== 'ready') {
    return (
      <AppShell>
        <div className={styles.loading} role="status">
          <Spinner />
          <span>Loading your organizations…</span>
        </div>
      </AppShell>
    );
  }

  return <>{children}</>;
}
